// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/ExecutivePosturePanel.jsx

import React from "react";
import { ActionGroup, Button } from "../../design-system/components";
import MissionControlMetric from "./MissionControlMetric";
import MissionControlSection from "./MissionControlSection";
import MissionControlStatus from "./MissionControlStatus";

function scoreTone(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return "neutral";
  if (value >= 90) return "success";
  if (value >= 75) return "warning";
  return "danger";
}

function riskLabel(tone, fallback) {
  if (tone === "low" || tone === "ok") return "Low Risk";
  if (tone === "medium" || tone === "warn") return "Medium Risk";
  if (tone === "high" || tone === "danger") return "High Risk";
  return fallback || "Posture unavailable";
}

export default function ExecutivePosturePanel({
  metrics,
  risk,
  formatSignedNumber,
  formatRelativeTime,
  formatTimestamp,
  refreshing = false,
  refreshMessage = "",
  refreshError = "",
  onRefresh,
  onOpenScoreDetails,
  onOpenTopRisks,
  onOpenAcceptedRisk,
  onOpenClosedRecords,
  onOpenAlerts,
  onOpenStaleControls,
}) {
  const operationalScore = typeof metrics?.operationalScore === "number" && Number.isFinite(metrics.operationalScore) ? metrics.operationalScore : null;
  const rawScore = typeof metrics?.rawScore === "number" && Number.isFinite(metrics.rawScore) ? metrics.rawScore : null;
  const operationalTone = scoreTone(operationalScore);
  const ringDegrees = Math.max(0, Math.min(360, (operationalScore ?? 0) * 3.6));

  return (
    <MissionControlSection
      eyebrow="Executive posture"
      title="Current operational position"
      description="A governed view of measured posture, accepted risk, active work, and data confidence."
      className="executive-posture-panel"
      actions={
        <ActionGroup>
          <Button
            variant="secondary"
            size="small"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? "Refreshing…" : "Refresh view"}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={onOpenScoreDetails}
          >
            Score details
          </Button>
        </ActionGroup>
      }
    >
      {refreshMessage || refreshError ? (
        <div
          className={`executive-posture-panel__refresh-notice ${
            refreshError
              ? "executive-posture-panel__refresh-notice--error"
              : ""
          }`}
          role={refreshError ? "alert" : "status"}
        >
          {refreshError || refreshMessage}
        </div>
      ) : null}

      <div className="executive-posture-panel__layout">
        <div className="executive-posture-score">
          <div
            className={`executive-posture-score__ring executive-posture-score__ring--${operationalTone}`}
            style={{ "--mission-score-degrees": `${ringDegrees}deg` }}
          >
            <div className="executive-posture-score__center">
              <span>Operational score</span>
              <strong className={operationalScore === null ? "executive-posture-score__unassessed" : undefined}>{operationalScore === null ? "Not assessed" : `${operationalScore.toFixed(1)}%`}</strong>
              <div className="executive-posture-score__status">
                <MissionControlStatus tone={risk?.tone}>
                  {riskLabel(risk?.tone, risk?.label)}
                </MissionControlStatus>
              </div>
            </div>
          </div>

          <div className="executive-posture-score__confidence">
            <span>Data as of</span>
            <strong>{formatRelativeTime(metrics?.dataAsOf)}</strong>
            <small>{formatTimestamp(metrics?.dataAsOf)}</small>
          </div>
        </div>

        <div className="executive-posture-panel__metrics-grid">
          <MissionControlMetric
            label="Raw score"
            value={rawScore === null ? "Not assessed" : `${rawScore.toFixed(1)}%`}
            supportingText="All measured findings included"
            tone={scoreTone(rawScore)}
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Operational score"
            value={operationalScore === null ? "Not assessed" : `${operationalScore.toFixed(1)}%`}
            supportingText={`${metrics?.acceptedRiskFindings || 0} accepted-risk finding${metrics?.acceptedRiskFindings === 1 ? "" : "s"} separated`}
            tone={operationalTone}
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Operational delta"
            value={formatSignedNumber(metrics?.operationalScoreDelta)}
            supportingText="Compared with prior operational score"
            tone={
              metrics?.operationalScoreDelta > 0
                ? "success"
                : metrics?.operationalScoreDelta < 0
                ? "danger"
                : "neutral"
            }
            onClick={onOpenScoreDetails}
          />
          <MissionControlMetric
            label="Active findings"
            value={metrics?.activeFindings || 0}
            supportingText="Prioritized for review"
            tone={metrics?.activeFindings > 0 ? "danger" : "success"}
            onClick={onOpenTopRisks}
          />
          <MissionControlMetric
            label="Open alerts"
            value={metrics?.openAlerts || 0}
            supportingText="Open, acknowledged, or snoozed"
            tone={metrics?.openAlerts > 0 ? "danger" : "success"}
            onClick={onOpenAlerts}
          />
          <MissionControlMetric
            label="Stale Controls"
            value={metrics?.staleCount || 0}
            supportingText="Stale or never-observed rule evidence"
            tone={metrics?.staleCount > 0 ? "warning" : "success"}
            onClick={onOpenStaleControls}
          />
          <MissionControlMetric
            label="Accepted risk"
            value={metrics?.acceptedRiskFindings || 0}
            supportingText="Approved exceptions"
            tone={metrics?.acceptedRiskFindings > 0 ? "warning" : "success"}
            onClick={onOpenAcceptedRisk}
          />
          <MissionControlMetric
            label="Closed records"
            value={metrics?.closedTasks || 0}
            supportingText="Audit history preserved"
            tone={metrics?.closedTasks > 0 ? "success" : "neutral"}
            onClick={onOpenClosedRecords}
          />
        </div>
      </div>
    </MissionControlSection>
  );
}
